export type RawNotificationPayload = any

export type ParsedNotification = Readonly<{
    title?: string
    body?: string
    messageId: string
    wasTapped: boolean
}>

function firstNonEmptyString(...candidates: unknown[]): string | undefined {
    for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim()) {
            return candidate
        }
    }
    return undefined
}

function parseDataField(payload: RawNotificationPayload): RawNotificationPayload | null {
    const data = payload.data
    if (!data) { return null }

    if (typeof data === "string") {
        try {
            return JSON.parse(data)
        } catch {
            return null
        }
    }

    return typeof data === "object" ? data : null
}

// Pulls title/body/id out of the differing payload shapes Firebase, APNs and GCM may deliver
export function extractNotificationData(payload: RawNotificationPayload | null | undefined): ParsedNotification {
    if (!payload) {
        return { messageId: "", wasTapped: false }
    }

    const data = parseDataField(payload)

    const title = firstNonEmptyString(
        data?.title,
        data?.header,
        payload.title,
        payload.header,
        payload.notification_title,
        payload.notification?.title,
        payload.notification?.header,
        payload["gcm.notification.title"],
        payload["gcm.notification.header"],
        payload.aps?.alert?.title,
        typeof payload.aps?.alert === "string" ? payload.aps.alert : undefined,
        payload.subject,
    )

    const body = firstNonEmptyString(
        data?.body,
        data?.message,
        payload.body,
        payload.message,
        payload.notification_body,
        payload.notification?.body,
        payload.notification?.message,
        payload["gcm.notification.body"],
        payload["gcm.notification.message"],
        payload.aps?.alert?.body,
    )

    const messageId = firstNonEmptyString(
        data?.id !== undefined ? String(data.id) : undefined,
        data?.messageId !== undefined ? String(data.messageId) : undefined,
        payload.messageId !== undefined ? String(payload.messageId) : undefined,
        payload.id !== undefined ? String(payload.id) : undefined,
    ) ?? ""

    // firebasex sets `tap` truthy when a notification is tapped from background / killed state.
    const tap = payload.tap ?? data?.tap
    const wasTapped = tap === true || tap === "true" || tap === "background"

    return { title, body, messageId, wasTapped }
}
